import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import Table from '~components/molecules/Table';
import Pagination from '~components/atoms/Pagination';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';

import * as Styles from './CustomerManagementCustomerTransferTable.module.scss';
import { TDeleteCustomerCollateralReduxParams } from '~types/feat/customer';
import { MetalTypeEnum, ModuleEnum, PermissionsEnum, TransactionStatusEnum } from '~utils/enums';
import Chip from '~components/atoms/Chip';
import ActionButton from '~components/atoms/ActionButton';
import { TCustomerTransferListing, TGetCustomerDetailsData, TGetCustomerTransferDetailsAPIParams } from '~types/feat/customerTransfer';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

const CustomerManagementCustomerTransferTable = () => {
    const { Misc: { dateTimeFormat } } = UtilFunctions;

    const dispatch = useAppDispatch();

    const isLoading = useAppSelector(state => state.customerTransfer.loadings.getCustomerTransferListing);
    const { index, maxIndex, data: customerTransfers } = useAppSelector(state => state.customerTransfer.customerTransferListing);
    const customerId = useAppSelector(state => state.customer.customerDetails.customerId);
    const userAccess = useAppSelector(state => state.auth.userDetails.access);

    const hasEditAccess = !!userAccess.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.CustomerManagement);

    const setCustomerTransferListingModalIsOpen = (state: boolean) => dispatch(combinedActions.customerTransferSetIsCustomerTransferDetailsModalOpen(state));
    const setSelectedCustomerTransfer = (customerTransfer: TCustomerTransferListing) => dispatch(combinedActions.customerTransferSetSelectedCustomerTransfer(customerTransfer));

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    useEffect(() => {
        dispatch(combinedActions.customerTransferGetCustomerTransferListingAttempt({ customerId }));
    }, [customerId]);

    const setIndex = (idx: number) => dispatch(combinedActions.customerSetCustomerCollateralIndex({ index: idx }));

    // if maxIndex is lower then index reset index to 0
    useEffect(() => {
        if (index > maxIndex) {
            setIndex(0);
        }
    }, [maxIndex]);

    const handleOnPrev = () => {
        if (index > 0) setIndex(index - 1);
    };
    const handleOnNext = () => {
        if (index < maxIndex) setIndex(index + 1);
    };
    const handleOnFirst = () => setIndex(0);
    const handleOnLast = () => setIndex(maxIndex >= 0 ? maxIndex : 0);

    const handleViewCustomerTransfer = (transaction: TCustomerTransferListing) => {
        dispatch(combinedActions.customerTransferGetCustomerTransferDetailsAttempt({ id: transaction.customerTransferId }));
        setCustomerTransferListingModalIsOpen(true);
    };

    const tableHeader = [
        'TRANSACTION ID',
        'SENDER NAME',
        'SENDER CIF',
        'PRODUCT NAME',
        'METAL TYPE',
        'SERIAL NUMBER',
        'TOTAL WEIGHT (G)',
        'RECEIVER NAME',
        'RECEIVER CIF',
        'BRANCH CODE',
        'BRANCH NAME',
        'BRANCH TELLER NAME',
        'SALES PRESONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'ACTION',
    ];

    const tableBody = customerTransfers.map(customerTransfer => {
        const {
            customerTransferId,
            senderName,
            senderCIFNo,
            productName,
            metalType,
            serialNumbers,
            weight,
            status,
            receiverName,
            receiverCIFNo,
            branchCode,
            branchName,
            regionName,
            branchTellerName,
            salesPersonnelName,
            referralName,
            createdAt,
        } = customerTransfer;

        const renderStatusChip = (statusValue: TransactionStatusEnum, label: string) => {
            switch (statusValue) {
                case TransactionStatusEnum.Pending: {
                    return (
                        <Chip
                            type='warning'
                            label={label}
                        />
                    );
                }

                case TransactionStatusEnum.Approved: {
                    return (
                        <Chip
                            type='success'
                            label={label}
                        />
                    );
                }

                case TransactionStatusEnum.Rejected: {
                    return (
                        <Chip
                            type='error'
                            label={label}
                        />
                    );
                }

                default: return <Chip label={label} />;
            }
        };

        const handleViewSerialNo = () => {
            setIsSerialNoModalOpen(true);
            setSerialNoList(serialNumbers);
        };

        const renderSerialNo = () => {
            return (
                <ActionButton
                    testId='approval-buy-sell-view-serial-number-modal-button'
                    key={customerTransferId}
                    label='View'
                    onClick={() => handleViewSerialNo()}
                />
            );
        };

        const massagedData = [
            customerTransferId,
            senderName,
            senderCIFNo,
            productName,
            MetalTypeEnum[metalType],
            renderSerialNo(),
            weight.toFixed(3),
            receiverName,
            receiverCIFNo,
            branchCode,
            branchName,
            branchTellerName,
            salesPersonnelName,
            referralName,
            dateTimeFormat(createdAt),
            (
                <ActionButton
                    testId='customer-transactionDetails-view-details-button'
                    label='View Details'
                    onClick={() => handleViewCustomerTransfer(customerTransfer)}
                />
            )
        ];

        return [
            ...massagedData.map(field => field ?? '-'),
        ];
    });

    return (
        <div className={Styles.container}>
            <Text
                label='Customer Transfers'
                isBold
            />

            <Table
                isLoading={isLoading}
                header={tableHeader}
                body={tableBody}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />

            <Pagination
                currentPage={index}
                maxPage={maxIndex}
                onPrev={handleOnPrev}
                onNext={handleOnNext}
                onFirst={handleOnFirst}
                onLast={handleOnLast}
            />
        </div>
    );
};

export default CustomerManagementCustomerTransferTable;
